import { useRef, useState, type FormEvent } from "react";
import type { Category, CategoryFilter } from "../../../shared/types";
import { HabitAddExpanded } from "./HabitAddExpanded";

interface HabitFormProps {
  readonly categoryFilter: CategoryFilter;
  readonly onAdd: (title: string, category: Category, frequency: "daily" | "weekly") => void;
}

export function HabitForm({ categoryFilter, onAdd }: HabitFormProps) {
  const [value, setValue] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category>(
    categoryFilter === "all" ? "personal" : categoryFilter,
  );
  const [frequency, setFrequency] = useState<"daily" | "weekly">("daily");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const showCategoryPicker = categoryFilter === "all";
  const isFilled = value.trim().length > 0;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    const cat = categoryFilter === "all" ? selectedCategory : categoryFilter;
    onAdd(trimmed, cat, frequency);
    setValue("");
    setFrequency("daily");
    setIsExpanded(false);
    inputRef.current?.focus();
  }

  function handleCancel() {
    setValue("");
    setIsExpanded(false);
    setFrequency("daily");
  }

  return (
    <form
      onSubmit={handleSubmit}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && !isFilled) {
          setIsExpanded(false);
        }
      }}
      className="bg-surface border border-border/60 rounded-lg overflow-hidden transition-shadow focus-within:ring-2 focus-within:ring-accent/20 focus-within:border-accent/40"
    >
      <label htmlFor="habit-add" className="sr-only">
        Nouvelle habitude
      </label>
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          ref={inputRef}
          id="habit-add"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsExpanded(true)}
          placeholder="Nouvelle habitude..."
          autoComplete="off"
          className="flex-1 bg-transparent border-none text-sm placeholder:text-ink-faint focus:outline-none"
        />
        <button
          type="submit"
          disabled={!isFilled}
          className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all min-h-[36px] touch-feedback ${
            isFilled
              ? "bg-accent text-white hover:bg-accent/90"
              : "bg-surface-alt text-ink-muted"
          } focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label={isFilled ? "Créer l'habitude" : "Entrer pour créer"}
        >
          {isFilled ? "→" : "+"}
        </button>
      </div>

      {/* Options étendues avec bouton annuler */}
      {(isExpanded || isFilled) && (
        <div className="px-3 pb-3">
          <HabitAddExpanded
            showCategoryPicker={showCategoryPicker}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            frequency={frequency}
            onFrequencyChange={setFrequency}
          />
          {isFilled && (
            <button
              type="button"
              onClick={handleCancel}
              className="mt-2 w-full py-2 text-xs text-ink-muted hover:text-ink hover:bg-surface-alt rounded-lg transition-colors min-h-[36px] touch-feedback"
            >
              Annuler
            </button>
          )}
        </div>
      )}
    </form>
  );
}
